import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { UserState } from '../states/user-state/user.state';
import { RightType } from './enums/rightType';

@Injectable({ providedIn: 'root' })
export class RightsService {
  private rights!: string[];

  public constructor(private store: Store) {
    this.store.select(UserState.getUserRights).subscribe((rights) => {
      this.rights = rights;
    });
  }

  public can(right: RightType): boolean {
    if (this.rights === undefined || this.rights === null) {
      return false;
    }

    return this.rights.some((x) => x === right);
  }

  public canSeeAdminPage(): boolean {
    return this.can(RightType.adminCanSeeAdminPage);
  }

  public canSeeCustomers(): boolean {
    return this.can(RightType.customersCanSeeCustomers);
  }

  public canMaintainCustomers(): boolean {
    return this.can(RightType.customersCanMaintainCustomers);
  }

  public canActivateDeactivateCustomers(): boolean {
    return this.can(RightType.customersCanActivateDeactivateCustomers);
  }

  public canMaintainUsersOfCustomers(): boolean {
    return this.can(RightType.customersCanMaintainUsersOfCustomers);
  }

  public canDeleteUsersOfCustomers(): boolean {
    return this.can(RightType.customersCanDeleteUsersOfCustomers);
  }

  public canExportUsersOfCustomers(): boolean {
    return this.can(RightType.customersCanExportUsersOfCustomers);
  }

  public canMaintainMappingsOfCustomers(): boolean {
    return this.can(RightType.customersCanMaintainMappingsOfCustomers);
  }

  public canDeleteMappingsOfCustomers(): boolean {
    return this.can(RightType.customersCanDeleteMappingsOfCustomers);
  }

  public canExportMappingsOfCustomers(): boolean {
    return this.can(RightType.customersCanExportMappingsOfCustomers);
  }

  public canMaintainMappingValuesOfCustomers(): boolean {
    return this.can(RightType.customersCanMaintainMappingValuesOfCustomers);
  }

  public canDeleteMappingValuesOfCustomers(): boolean {
    return this.can(RightType.customersCanDeleteMappingValuesOfCustomers);
  }

  public canExportMappingValuesOfCustomers(): boolean {
    return this.can(RightType.customersCanExportMappingValuesOfCustomers);
  }

  public canSetPluginsFlowOfCustomers(): boolean {
    return this.can(RightType.customersCanSetPluginsFlowOfCustomers);
  }

  public canActivateDeactivateFlowOfCustomers(): boolean {
    return this.can(RightType.customersCanActivateDeactivateFlowOfCustomers);
  }

  public canMaintainActionsOfCustomers(): boolean {
    return this.can(RightType.customersCanMaintainActionsOfCustomers);
  }

  public canDeleteActionsOfCustomers(): boolean {
    return this.can(RightType.customersCanDeleteActionsOfCustomers);
  }

  public canSeeTasks(): boolean {
    return this.can(RightType.tasksCanSeeTasks);
  }

  public canActionTasks(): boolean {
    return this.can(RightType.tasksCanActionTasks);
  }

  public canDeleteTasks(): boolean {
    return this.can(RightType.tasksCanDeleteTasks);
  }

  public canCanSeeSystemLogs(): boolean {
    return this.can(RightType.adminCanSeeSystemLogs);
  }

  public canMaintainRoles(): boolean {
    return this.can(RightType.adminCanMaintainRoles);
  }

  public canDeleteRoles(): boolean {
    return this.can(RightType.adminCanDeleteRoles);
  }

  public canChangeAdminSettings(): boolean {
    return this.can(RightType.adminCanChangeAdminSettings);
  }

  public canPauseTasks(): boolean {
    return this.can(RightType.tasksCanPauseTasks);
  }

  public canResendTask(): boolean {
    return this.can(RightType.tasksCanResend);
  }

  public canSeeUsers(): boolean {
    return this.can(RightType.adminCanSeeUsers);
  }

  public canMaintainUsers(): boolean {
    return this.can(RightType.adminCanMaintainUsers);
  }

  public canDeleteUsers(): boolean {
    return this.can(RightType.adminCanDeleteUsers);
  }

  public canSeePlugins(): boolean {
    return this.can(RightType.adminCanSeePlugins);
  }

  public canMaintainPlugins(): boolean {
    return this.can(RightType.adminCanMaintainPlugins);
  }

  public canLoadPlugins(): boolean {
    return this.can(RightType.adminCanLoadPlugins);
  }

  public canEnableDisablePlugins(): boolean {
    return this.can(RightType.adminCanEnableDisablePlugins);
  }

  public canDeletePlugins(): boolean {
    return this.can(RightType.adminCanDeletePlugins);
  }

  public canUpdatePluginsDisplayName(): boolean {
    return this.can(RightType.adminCanUpdatePluginsDisplayName);
  }

  public canSeeActions(): boolean {
    return this.can(RightType.adminCanSeeActions);
  }

  public canMaintainActions(): boolean {
    return this.can(RightType.adminCanMaintainActions);
  }

  public canSeeLaboratories(): boolean {
    return this.can(RightType.adminCanSeeLaboratories);
  }

  public canAddLaboratories(): boolean {
    return this.can(RightType.adminCanAddLaboratories);
  }

  public canMaintainLaboratories(): boolean {
    return this.can(RightType.adminCanMaintainLaboratories);
  }

  public canDeleteLaboratories(): boolean {
    return this.can(RightType.adminCanDeleteLaboratories);
  }

  public canSeeRegions(): boolean {
    return this.can(RightType.adminCanSeeRegions);
  }

  public canMaintainRegions(): boolean {
    return this.can(RightType.adminCanMaintainRegions);
  }

  public canDeleteRegions(): boolean {
    return this.can(RightType.adminCanDeleteRegions);
  }

  public canAdminCanMaintainRegionEsbs(): boolean{
    return this.can(RightType.adminCanMaintainRegionEsbs);
  }

  public canAdminCanMaintainLaboratoryEsbs(): boolean{
    return this.can(RightType.adminCanMaintainLaboratoryEsbs);
  }

  public canAdminCanMaintainLaboratoryDatabases(): boolean {
    return this.can(RightType.adminCanMaintainLaboratoryDatabases);
  }

  public canAdminCanMaintainLaboratorySettings(): boolean {
    return this.can(RightType.adminCanMaintainLaboratorySettings);
  }

  public canAdminCanSeeRoles(): boolean {
    return this.can(RightType.adminCanSeeRoles);
  }
}
